import { useMemo, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonInput,
  IonToggle,
  IonText,
  IonIcon,
  IonSpinner,
  IonPopover,
} from '@ionic/react';
import { informationCircleOutline } from 'ionicons/icons';
import { MAX_PLAYERS_MARKET_MAX, MAX_PLAYERS_MARKET_MIN } from '@/appConfig';
import { LEAGUE_INFO_CONTENT, type LeagueInfoKey } from '@/constants/leagueInfoContent';


export type LeagueConfigDraft = {
  name: string;
  isPublic: boolean;
  maxPlayers: number | null;
};

interface Props {
  isOpen: boolean;
  loading?: boolean;
  initial: LeagueConfigDraft | null; // current league values (prefill)
  onClose: () => void;
  onSave: (next: LeagueConfigDraft) => void;
}

export function EditFantasyLeagueConfigModal({
  isOpen,
  loading = false,
  initial,
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxPlayers, setMaxPlayers] = useState<number>(MAX_PLAYERS_MARKET_MIN);
  const [errors, setErrors] = useState<string[]>([]);

  // Popover state (same as CreateFantasyLeague)
  const [infoOpen, setInfoOpen] = useState<LeagueInfoKey | null>(null);
  const [infoEvent, setInfoEvent] = useState<MouseEvent | undefined>(undefined);

  const openInfo = (key: LeagueInfoKey) => (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    setInfoEvent(ev.nativeEvent);
    setInfoOpen(key);
  };

  const closeInfo = () => {
    setInfoOpen(null);
    setInfoEvent(undefined);
  };

  // Prefill when opening (mirrors expected UX)
  const handleWillPresent = () => {
    if (!initial) return;

    setName(initial.name ?? '');
    setIsPublic(Boolean(initial.isPublic));

    const initialMax =
      initial.maxPlayers == null ? MAX_PLAYERS_MARKET_MIN : initial.maxPlayers;

    let bounded = initialMax;
    if (bounded < MAX_PLAYERS_MARKET_MIN) bounded = MAX_PLAYERS_MARKET_MIN;
    if (bounded > MAX_PLAYERS_MARKET_MAX) bounded = MAX_PLAYERS_MARKET_MAX;

    setMaxPlayers(bounded);
    setErrors([]);
  };

  const handleMaxPlayersInput = (value: string | number | null | undefined) => {
    if (value === '' || value === null || value === undefined) return;

    const n = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(n)) return;

    let bounded = n;
    if (bounded < MAX_PLAYERS_MARKET_MIN) bounded = MAX_PLAYERS_MARKET_MIN;
    if (bounded > MAX_PLAYERS_MARKET_MAX) bounded = MAX_PLAYERS_MARKET_MAX;

    setMaxPlayers(bounded);
  };

  // "Dirty" like you’d expect: disable Save if no changes
  const isDirty = useMemo(() => {
    if (!initial) return false;

    const initName = initial.name ?? '';
    const initIsPublic = Boolean(initial.isPublic);

    const initMax =
      initial.maxPlayers == null ? MAX_PLAYERS_MARKET_MIN : initial.maxPlayers;

    let boundedInitMax = initMax;
    if (boundedInitMax < MAX_PLAYERS_MARKET_MIN) boundedInitMax = MAX_PLAYERS_MARKET_MIN;
    if (boundedInitMax > MAX_PLAYERS_MARKET_MAX) boundedInitMax = MAX_PLAYERS_MARKET_MAX;

    return (
      name !== initName ||
      isPublic !== initIsPublic ||
      maxPlayers !== boundedInitMax
    );
  }, [initial, name, isPublic, maxPlayers]);

  const canSave = Boolean(initial) && isDirty && !loading && Boolean(name.trim());

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrors([]);

    if (!name.trim()) {
      setErrors(['El nombre de la liga es requerido']);
      return;
    }

    onSave({
      name: name.trim(),
      isPublic,
      maxPlayers,
    });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} onWillPresent={handleWillPresent}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Editar configuración</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} disabled={loading}>
              Cerrar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div className="mt-4">
          <form onSubmit={handleSubmit}>
            {!initial ? (
              <div className="flex justify-center items-center min-h-[160px]">
                <IonSpinner name="crescent" />
              </div>
            ) : (
              <>
                <IonList>
                  {/* Nombre */}
                  <IonItem className="items-center">
                    <div className="w-full flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <IonButton
                          fill="clear"
                          size="small"
                          className="p-0 h-auto"
                          onClick={openInfo('name')}
                          aria-label="Información sobre el nombre de la liga"
                        >
                          <IonIcon icon={informationCircleOutline} />
                        </IonButton>

                        <span className="w-44 whitespace-nowrap">Nombre de la Liga *</span>
                      </div>

                      <IonInput
                        className="w-40 text-start"
                        value={name}
                        onIonInput={(e) => setName((e.detail.value ?? '').toString())}
                        placeholder="Ej: Liga de Amigos"
                        required
                        disabled={loading}
                      />
                    </div>
                  </IonItem>

                  {/* Pública */}
                  <IonItem>
                    <div className="w-full flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <IonButton
                          fill="clear"
                          size="small"
                          className="p-0 h-auto"
                          onClick={openInfo('public')}
                          aria-label="Información sobre liga pública"
                        >
                          <IonIcon icon={informationCircleOutline} />
                        </IonButton>

                        <span className="w-44 whitespace-nowrap">Liga Pública</span>
                      </div>

                      <IonToggle
                        checked={isPublic}
                        onIonChange={(e) => setIsPublic(Boolean(e.detail.checked))}
                        disabled={loading}
                      />
                    </div>
                  </IonItem>

                  {/* Máx jugadores */}
                  <IonItem className="items-center">
                    <div className="w-full flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <IonButton
                          fill="clear"
                          size="small"
                          className="p-0 h-auto"
                          onClick={openInfo('maxPlayers')}
                          aria-label="Información sobre máximo de jugadores"
                        >
                          <IonIcon icon={informationCircleOutline} />
                        </IonButton>

                        <span className="w-44 whitespace-nowrap">Máx. Jugadores Mercado</span>
                      </div>

                      <IonInput
                        className="w-20 text-left"
                        type="number"
                        inputmode="numeric"
                        min={MAX_PLAYERS_MARKET_MIN}
                        max={MAX_PLAYERS_MARKET_MAX}
                        value={maxPlayers}
                        onIonInput={(e) => handleMaxPlayersInput(e.detail.value)}
                        disabled={loading}
                      />
                    </div>
                  </IonItem>
                </IonList>

                {/* Popover compartido */}
                <IonPopover
                  isOpen={infoOpen !== null}
                  event={infoEvent}
                  onDidDismiss={closeInfo}
                >
                  <div className="p-3 max-w-[320px]">
                    {infoOpen && (
                      <>
                        <div className="text-sm font-semibold mb-1">
                          {LEAGUE_INFO_CONTENT[infoOpen].title}
                        </div>
                        <div className="text-sm text-gray-600">
                          {LEAGUE_INFO_CONTENT[infoOpen].body}
                        </div>
                      </>
                    )}
                  </div>
                </IonPopover>

                {errors.length > 0 && (
                  <div className="ion-padding">
                    {errors.map((error, index) => (
                      <IonText color="danger" key={index}>
                        <p className="text-sm">{error}</p>
                      </IonText>
                    ))}
                  </div>
                )}

                <div className="ion-padding">
                  <IonButton expand="block" type="submit" disabled={!canSave}>
                    {loading ? <IonSpinner name="crescent" /> : 'Guardar cambios'}
                  </IonButton>

                  {!isDirty && (
                    <IonText color="medium">
                      <p className="text-sm text-center mt-2">No hay cambios para guardar.</p>
                    </IonText>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </IonContent>
    </IonModal>
  );
}
